/**
 * cardScrollForwarding
 *
 * The RMS form renderer keeps the question card inside a centred column
 * (md:w-1/2) within a fixed-height viewport, so the card's internal scroll
 * area only occupies the middle of the page. UAT feedback: wheel/trackpad
 * scrolling did nothing unless the pointer was over that centre column.
 *
 * These helpers let a wider ancestor (the full-width question row) forward
 * wheel events to the active card's scroll container, so scrolling works with
 * the pointer anywhere over the question area, including the side gutters.
 *
 * The card's scroll container is marked with the `data-rms-card-scroll`
 * attribute (see layout/QuestionCard.js). During a card transition two cards
 * exist at once (exiting then entering); the entering/active card renders
 * last in the DOM, so the last match is used.
 */

export const CARD_SCROLL_ATTRIBUTE = 'data-rms-card-scroll';

/**
 * Find the active question card's scroll container within rootEl.
 * Returns null when no card is mounted.
 */
export function getActiveCardScrollElement(rootEl) {
  if (!rootEl || typeof rootEl.querySelectorAll !== 'function') return null;
  const matches = rootEl.querySelectorAll(`[${CARD_SCROLL_ATTRIBUTE}]`);
  return matches.length ? matches[matches.length - 1] : null;
}

/**
 * Forward a wheel event to the active card's scroll container.
 *
 * @param {HTMLElement} rootEl - ancestor that received the wheel event
 * @param {WheelEvent|SyntheticEvent} event - the wheel event (needs target,
 *   deltaY and deltaMode)
 * @returns {boolean} true when the card was scrolled, false when the event
 *   was left for native handling (or there was nothing to scroll)
 */
export function forwardWheelToActiveCard(rootEl, event) {
  if (!rootEl || !event) return false;
  const target = event.target;
  // Ignore events bubbling (via the React tree) from portalled overlays such
  // as dialogs or dropdown lists - their DOM nodes live outside rootEl and
  // they manage their own scrolling.
  if (target && !rootEl.contains(target)) return false;
  const el = getActiveCardScrollElement(rootEl);
  if (!el) return false;
  // Pointer is already over the card's own scroll area: native scrolling
  // handles it, and forwarding as well would double-scroll.
  if (target && el.contains(target)) return false;
  // Nothing to scroll (content fits the viewport).
  if ((el.scrollHeight || 0) <= (el.clientHeight || 0)) return false;
  // Normalise the delta: deltaMode 0 = pixels, 1 = lines, 2 = pages.
  const unit = event.deltaMode === 1 ? 16 : event.deltaMode === 2 ? el.clientHeight || 0 : 1;
  const delta = (event.deltaY || 0) * unit;
  if (!delta) return false;
  el.scrollTop += delta;
  return true;
}
